import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { applyMark } from "./format.js";
import { QUIET_CLASS } from "./styles.js";
/** Text of the element, with non-breaking spaces as plain ones; one line unless multiline. */
const readText = (element, multiline) => {
    const raw = (element.textContent ?? "").replace(/\u00a0/g, " ");
    return multiline ? raw : raw.replace(/[\r\n]+/g, " ");
};
/** Characters from the start of `element` to the boundary (`node`, `offset`). */
const offsetOf = (element, node, offset) => {
    const range = element.ownerDocument.createRange();
    range.selectNodeContents(element);
    range.setEnd(node, offset);
    return range.toString().length;
};
/** The text node and offset `at` characters into `element`; its end when past the text. */
const pointAt = (element, at) => {
    const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let left = at;
    for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
        const length = node.textContent?.length ?? 0;
        if (left <= length) {
            return [node, left];
        }
        left -= length;
    }
    return [element, element.childNodes.length];
};
const MARKS = [
    { mark: "bold", sign: "B", name: "Fett (Strg+B)", className: "w-8 font-bold" },
    { mark: "italic", sign: "I", name: "Kursiv (Strg+I)", className: "w-8 font-serif italic" },
    { mark: "list", sign: "\u2022", name: "Liste", className: "w-8" },
    { mark: "link", sign: "Link", name: "Link", className: "underline" },
];
/**
 * A text of the participant page edited in place (`contentEditable="plaintext-only"`), taken
 * over from the page editor of the customer portal. While it has the focus the DOM holds the
 * text; a new value from outside only lands without focus, so the caret never jumps. Focus is
 * tracked by onFocus/onBlur: inside the preview frame `document.activeElement` is the iframe
 * itself. `whitespace-pre-wrap` keeps spaces and line breaks as text instead of `&nbsp;`/`<br>`.
 *
 * While empty it shows `placeholder`: with `placeholderTone="page"` the text the page shows
 * without an own one, otherwise a faint hint for the editor only.
 *
 * In the door editor since D37, so the backoffice and the sponsor portal edit the same way;
 * styled with the utilities of the shared Tailwind preset, which both surfaces load.
 */
export function EditableText({ id, value, onChange, label, placeholder, placeholderTone = "hint", multiline = false, maxLength, readOnly = false, formatting, "aria-invalid": invalid, "aria-describedby": describedBy, }) {
    const ref = useRef(null);
    const focused = useRef(false);
    const wrapper = useRef(null);
    /** Where the bar stands over the selection, relative to the wrapper; `null` hides it. */
    const [bar, setBar] = useState(null);
    const formats = formatting !== undefined && !readOnly;
    useEffect(() => {
        const element = ref.current;
        if (!formats || element === null) {
            return;
        }
        // The element's own document: inside the preview frame, the frame's.
        const doc = element.ownerDocument;
        const place = () => {
            const selection = doc.getSelection();
            const box = wrapper.current?.getBoundingClientRect();
            const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
            if (!focused.current || !box || range === null || range.collapsed || !element.contains(range.commonAncestorContainer)) {
                setBar(null);
                return;
            }
            const rect = range.getBoundingClientRect();
            // Kept inside the wrapper: about half the bar's width either side of its middle.
            const half = 72;
            const left = Math.min(Math.max(rect.left - box.left + rect.width / 2, half), Math.max(box.width - half, half));
            setBar({ top: rect.top - box.top, left });
        };
        doc.addEventListener("selectionchange", place);
        return () => {
            doc.removeEventListener("selectionchange", place);
        };
    }, [formats]);
    useEffect(() => {
        const element = ref.current;
        if (element !== null && !focused.current && readText(element, multiline) !== value) {
            element.textContent = value;
        }
    }, [value, multiline]);
    const onInput = () => {
        const element = ref.current;
        if (element === null) {
            return;
        }
        // Firefox leaves a <br> in an emptied editable, and :empty would never match again.
        if (!element.textContent) {
            element.replaceChildren();
        }
        let next = readText(element, multiline);
        if (maxLength !== undefined && next.length > maxLength) {
            next = next.slice(0, maxLength);
            element.textContent = next;
        }
        onChange(next);
    };
    /** Puts a mark on the selection through the editing command, so Strg+Z takes it back. */
    const format = (mark) => {
        const element = ref.current;
        const selection = element?.ownerDocument.getSelection();
        if (!element || !selection || selection.rangeCount === 0) {
            return;
        }
        const range = selection.getRangeAt(0);
        const text = readText(element, true);
        const out = applyMark(mark, {
            text,
            start: offsetOf(element, range.startContainer, range.startOffset),
            end: offsetOf(element, range.endContainer, range.endOffset),
        });
        if (out.text === text) {
            return;
        }
        const doc = element.ownerDocument;
        const all = doc.createRange();
        all.selectNodeContents(element);
        selection.removeAllRanges();
        selection.addRange(all);
        // Deprecated, but the one way into the editor's undo history; the input event follows.
        if (!doc.execCommand("insertText", false, out.text)) {
            element.textContent = out.text;
            onInput();
        }
        const next = doc.createRange();
        next.setStart(...pointAt(element, out.start));
        next.setEnd(...pointAt(element, out.end));
        selection.removeAllRanges();
        selection.addRange(next);
    };
    const onKeyDown = (event) => {
        if (formats && (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey) {
            const key = event.key.toLowerCase();
            if (key === "b" || key === "i") {
                event.preventDefault();
                format(key === "b" ? "bold" : "italic");
                return;
            }
        }
        if (!multiline && event.key === "Enter" && !event.nativeEvent.isComposing) {
            event.preventDefault();
            event.currentTarget.blur();
        }
    };
    const classes = [
        "whitespace-pre-wrap outline-dashed outline-1 outline-offset-4 outline-transparent transition-[outline-color] focus:outline-brand",
        "empty:before:pointer-events-none empty:before:content-[attr(data-placeholder)]",
        placeholderTone === "hint" ? "empty:before:text-ink-muted empty:before:opacity-70" : "",
        multiline ? "block w-full" : "inline-block min-w-[2ch]",
        readOnly ? "cursor-default" : "cursor-text hover:outline-brand/50",
    ];
    const editor = (_jsx("span", { ref: ref, id: id, role: "textbox", tabIndex: 0, "aria-label": label, "aria-multiline": multiline || undefined, "aria-readonly": readOnly || undefined, "aria-invalid": invalid || undefined, "aria-describedby": describedBy, contentEditable: readOnly ? false : "plaintext-only", suppressContentEditableWarning: true, "data-placeholder": placeholder || label, onFocus: () => {
            focused.current = true;
        }, onBlur: () => {
            focused.current = false;
            setBar(null);
        }, onInput: onInput, onKeyDown: onKeyDown, className: classes.filter((part) => part !== "").join(" ") }));
    if (!formats) {
        return editor;
    }
    return (_jsxs("span", { ref: wrapper, className: multiline ? "relative block" : "relative inline-block", children: [editor, bar === null ? null : (_jsx("span", { role: "toolbar", "aria-label": "Formatierung", className: "absolute z-30 flex -translate-x-1/2 -translate-y-full gap-0.5 rounded-md border border-line bg-surface p-0.5 font-sans text-ink shadow-md", style: { top: bar.top - 6, left: bar.left }, 
                // Keeps the focus and the selection in the text while a mark goes on.
                onMouseDown: (event) => {
                    event.preventDefault();
                }, children: MARKS.filter((entry) => entry.mark !== "link" || formatting.links).map((entry) => (_jsx("button", { type: "button", className: `${QUIET_CLASS} ${entry.className}`, "aria-label": entry.name, title: entry.name, onClick: () => {
                        format(entry.mark);
                    }, children: entry.sign }, entry.mark))) }))] }));
}
//# sourceMappingURL=EditableText.js.map